/**
 * Back office → Studio (C14): posts with a visual and a short 9:16 video in the agency's colours,
 * the editorial calendar and the post ideas drawn from the prospects' frequent questions.
 * Generation runs on the server (Celery); this page shows the progress and the results.
 */

import {
  type ContentIdea,
  type ContentItem,
  type ContentItemRequest,
  type StudioKindEnum,
  type StudioMusicEnum,
  type StudioStatusEnum,
  type StudioTemplateEnum,
  type StudioThemeEnum,
  useStudioContentsCalendarList,
  useStudioContentsCreate,
  useStudioContentsIdeasCreate,
  useStudioContentsList,
  useStudioContentsMarkPublishedCreate,
  useStudioContentsPartialUpdate,
  useStudioContentsRenderVideoCreate,
  useStudioContentsRenderVisualCreate,
  useStudioContentsRetrieve,
  useStudioContentsScheduleCreate,
  useStudioIdeasAcceptCreate,
  useStudioIdeasDismissCreate,
  useStudioIdeasList,
  useWebsiteOffersList,
  useWebsiteTestimonialsList,
} from "@agencyos/api-client";
import {
  Badge,
  type BadgeTone,
  Button,
  GlassCard,
  GlassSheet,
  SelectField,
  Skeleton,
  SoftToggle,
  TextAreaField,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ChevronLeft, ChevronRight, Download, Film, ImageIcon, Lightbulb, Plus } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { dateText, dateTimeText, todayInDouala } from "../billing/shared";
import "./studio.css";

type Tab = "calendar" | "list" | "ideas";
const TABS: Tab[] = ["calendar", "list", "ideas"];
const KINDS: StudioKindEnum[] = ["offer", "testimonial", "tip", "announcement"];
const STATUSES: StudioStatusEnum[] = ["draft", "ready", "scheduled", "published"];
const TEMPLATES: StudioTemplateEnum[] = ["square", "story", "banner"];
const THEMES: StudioThemeEnum[] = ["crystal", "midnight"];
const MUSICS: StudioMusicEnum[] = ["none", "tone"];
const CHANNELS = ["facebook", "instagram", "whatsapp"] as const;
type Channel = (typeof CHANNELS)[number];
const STATUS_TONE: Record<StudioStatusEnum, BadgeTone> = { draft: "default", ready: "offer", scheduled: "success", published: "success" };
const BUSY = new Set(["queued", "running"]);
/** Douala is UTC+1 all year (no daylight saving): a local date-time input is read at +01:00. */
const DOUALA_OFFSET = "+01:00";

export function isBusy(state: string): boolean {
  return BUSY.has(state);
}

function meta(value: unknown, key: string): number | string | boolean | undefined {
  if (!value || typeof value !== "object") return undefined;
  const found = (value as Record<string, unknown>)[key];
  return typeof found === "number" || typeof found === "string" || typeof found === "boolean" ? found : undefined;
}

function useRefreshStudio() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/studio/") });
}

/** First day of the month (YYYY-MM-01) moved by `delta` months. */
export function monthStart(month: string, delta = 0): string {
  const [year, index] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 2026, (index ?? 1) - 1 + delta, 1));
  return date.toISOString().slice(0, 10);
}

/** Weeks of a month grid (Monday first): YYYY-MM-DD, or "" for the padding cells. */
export function monthGrid(start: string): string[][] {
  const first = new Date(`${start}T12:00:00Z`);
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const cells = [...Array<string>(lead).fill(""), ...Array.from({ length: days }, (_v, i) => `${start.slice(0, 8)}${String(i + 1).padStart(2, "0")}`)];
  while (cells.length % 7) cells.push("");
  const weeks: string[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function dayOf(iso: string | null): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Douala" }).format(new Date(iso));
}

// --- Calendar ---------------------------------------------------------------------------------

function CalendarTab({ onOpen }: { onOpen: (item: ContentItem) => void }) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => monthStart(todayInDouala().slice(0, 7)));
  const end = monthStart(month.slice(0, 7), 1);
  const query = useStudioContentsCalendarList({ start: month, end });
  const rows = useMemo(() => (query.data?.status === 200 ? query.data.data : []), [query.data]);
  const byDay = useMemo(() => {
    const out: Record<string, ContentItem[]> = {};
    for (const row of rows) {
      const day = dayOf(row.scheduled_at ?? row.published_at);
      (out[day] ??= []).push(row);
    }
    return out;
  }, [rows]);
  const label = new Date(`${month}T12:00:00Z`).toLocaleDateString(i18n.language, { month: "long", year: "numeric", timeZone: "UTC" });
  const weekdays = t("studio.calendar.weekdays").split(",");
  return (
    <GlassCard as="section" className="stack" aria-label={t("studio.calendar.month", { month: label })}>
      <div className="section-title">
        <h2>{label}</h2>
        <div className="studio-row">
          <Button variant="ghost" aria-label={t("studio.calendar.previous")} icon={<ChevronLeft size={16} aria-hidden="true" />} onClick={() => setMonth(monthStart(month.slice(0, 7), -1))} />
          <Button variant="ghost" aria-label={t("studio.calendar.next")} icon={<ChevronRight size={16} aria-hidden="true" />} onClick={() => setMonth(end)} />
        </div>
      </div>
      {query.isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <table className="studio-calendar">
          <thead>
            <tr>
              {weekdays.map((d) => (
                <th key={d} scope="col">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthGrid(month).map((week) => (
              <tr key={week.join("|")}>
                {week.map((day, index) => (
                  <td key={day || `pad-${index}`} className={day ? "" : "studio-calendar__pad"}>
                    {day ? <span className="studio-calendar__day">{Number(day.slice(8))}</span> : null}
                    {(byDay[day] ?? []).map((item) => (
                      <button key={item.id} type="button" className={`studio-chip studio-chip--${item.status}`} onClick={() => onOpen(item)}>
                        {item.title}
                      </button>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!query.isLoading && rows.length === 0 ? <p className="muted">{t("studio.calendar.nothing")}</p> : null}
    </GlassCard>
  );
}

// --- List -------------------------------------------------------------------------------------

function ListTab({ onOpen, onNew }: { onOpen: (item: ContentItem) => void; onNew: () => void }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<StudioStatusEnum | "">("");
  const query = useStudioContentsList(status ? { status } : {});
  const rows = query.data?.status === 200 ? query.data.data.results : [];
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <SelectField
          label={t("studio.list.filter")}
          value={status}
          onChange={(e) => setStatus(e.target.value as StudioStatusEnum | "")}
          options={[{ value: "", label: t("studio.list.all") }, ...STATUSES.map((s) => ({ value: s, label: t(`studio.status.${s}`) }))]}
        />
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={onNew}>
          {t("studio.new")}
        </Button>
      </div>
      {query.isLoading ? (
        <Skeleton style={{ height: 160 }} />
      ) : rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>{t("studio.list.title")}</th>
              <th>{t("studio.list.kind")}</th>
              <th>{t("studio.list.status")}</th>
              <th>{t("studio.list.when")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <button type="button" className="link" onClick={() => onOpen(row)}>
                    {row.title}
                  </button>
                </td>
                <td>{t(`studio.kind.${row.kind ?? "tip"}`)}</td>
                <td>
                  <Badge tone={STATUS_TONE[row.status]}>{t(`studio.status.${row.status}`)}</Badge>
                </td>
                <td>{row.scheduled_at ? dateTimeText(row.scheduled_at, i18n.language) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">{t("studio.empty")}</p>
      )}
    </GlassCard>
  );
}

// --- Ideas ------------------------------------------------------------------------------------

function IdeasTab({ onOpen }: { onOpen: (item: ContentItem) => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshStudio();
  const [waitingSince, setWaitingSince] = useState<number | null>(null);
  const query = useStudioIdeasList({ status: "proposed" }, { query: { refetchInterval: waitingSince ? 2000 : false } });
  const rows = useMemo<ContentIdea[]>(() => (query.data?.status === 200 ? query.data.data.results : []), [query.data]);
  const propose = useStudioContentsIdeasCreate();
  const accept = useStudioIdeasAcceptCreate();
  const dismiss = useStudioIdeasDismissCreate({ mutation: { onSuccess: refresh } });
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!waitingSince) return undefined;
    const newest = rows[0]?.created_at;
    if (newest && new Date(newest).getTime() >= waitingSince - 5000) setWaitingSince(null);
    const stop = window.setTimeout(() => setWaitingSince(null), 60_000);
    return () => window.clearTimeout(stop);
  }, [rows, waitingSince]);
  const language = i18n.language.startsWith("en") ? "en" : "fr";
  return (
    <GlassCard as="section" className="stack" aria-label={t("studio.ideas.title")}>
      <div className="section-title">
        <div>
          <h2>{t("studio.ideas.title")}</h2>
          <p className="muted">{t("studio.ideas.intro")}</p>
        </div>
        <Button
          icon={<Lightbulb size={16} aria-hidden="true" />}
          disabled={propose.isPending || waitingSince !== null}
          onClick={() => propose.mutate({ data: { language } }, { onSuccess: () => setWaitingSince(Date.now()) })}
        >
          {waitingSince !== null ? t("studio.ideas.working") : t("studio.ideas.propose")}
        </Button>
      </div>
      {message ? <p role="status">{message}</p> : null}
      {query.isLoading ? (
        <Skeleton style={{ height: 120 }} />
      ) : rows.length ? (
        <ul className="studio-ideas">
          {rows.map((idea) => (
            <li key={idea.id} className="studio-idea">
              <div className="studio-row">
                <strong>{idea.title}</strong>
                <Badge tone={idea.source === "ai" ? "offer" : "default"}>{t(`studio.ideas.source.${idea.source}`)}</Badge>
              </div>
              <p>{idea.text}</p>
              {idea.rationale ? <p className="muted">{idea.rationale}</p> : null}
              <div className="studio-row">
                <Button
                  onClick={() =>
                    accept.mutate(
                      { id: idea.id },
                      {
                        onSuccess: (r) => {
                          refresh();
                          setMessage(t("studio.ideas.accepted"));
                          if (r.status === 201) onOpen(r.data);
                        },
                      },
                    )
                  }
                >
                  {t("studio.ideas.accept")}
                </Button>
                <Button variant="ghost" onClick={() => dismiss.mutate({ id: idea.id })}>
                  {t("studio.ideas.dismiss")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("studio.ideas.empty")}</p>
      )}
    </GlassCard>
  );
}

// --- Editor -----------------------------------------------------------------------------------

interface Draft {
  kind: StudioKindEnum;
  title: string;
  text: string;
  language: "fr" | "en";
  template: StudioTemplateEnum;
  theme: StudioThemeEnum;
  offer: string;
  testimonial: string;
  event_date: string;
  voice_over: boolean;
  music: StudioMusicEnum;
}

function draftOf(item: ContentItem | undefined): Draft {
  return {
    kind: item?.kind ?? "tip",
    title: item?.title ?? "",
    text: item?.text ?? "",
    language: item?.language === "en" ? "en" : "fr",
    template: item?.template ?? "square",
    theme: item?.theme ?? "crystal",
    offer: item?.offer ?? "",
    testimonial: item?.testimonial ?? "",
    event_date: item?.event_date ?? "",
    voice_over: item?.voice_over ?? false,
    music: item?.music ?? "none",
  };
}

function payload(draft: Draft): ContentItemRequest {
  return {
    ...draft,
    offer: draft.kind === "offer" && draft.offer ? draft.offer : null,
    testimonial: draft.kind === "testimonial" && draft.testimonial ? draft.testimonial : null,
    event_date: draft.event_date || null,
  };
}

function Preview({ item, draft }: { item: ContentItem | undefined; draft: Draft }) {
  const { t } = useTranslation();
  if (item?.visual_url && !isBusy(item.visual_state)) {
    return <img className={`studio-preview studio-preview--${item.template ?? "square"}`} src={item.visual_url} alt={t("studio.visual.alt", { title: item.title })} />;
  }
  return (
    <figure className={`studio-preview studio-preview--${draft.template} studio-preview--${draft.theme}`} aria-label={t("studio.editor.preview")}>
      <div className="studio-preview__card">
        <strong>{draft.title || t(`studio.kind.${draft.kind}`)}</strong>
        <p>{draft.text}</p>
      </div>
      <figcaption className="muted">{t("studio.editor.previewHint")}</figcaption>
    </figure>
  );
}

function ScheduleBox({ item }: { item: ContentItem }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshStudio();
  const [when, setWhen] = useState("");
  const [channels, setChannels] = useState<Channel[]>(["facebook", "instagram"]);
  const schedule = useStudioContentsScheduleCreate({ mutation: { onSuccess: refresh } });
  const published = useStudioContentsMarkPublishedCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(schedule.error);
  const hasMedia = Boolean(item.visual_url || item.video_url);
  const toggle = (channel: Channel, on: boolean) => setChannels((list) => (on ? [...new Set([...list, channel])] : list.filter((c) => c !== channel)));
  return (
    <section className="stack" aria-label={t("studio.schedule.title")}>
      <h3>{t("studio.schedule.title")}</h3>
      {item.status === "scheduled" && item.scheduled_at ? (
        <p role="status">
          {t("studio.schedule.done", { date: dateTimeText(item.scheduled_at, i18n.language) })}{" "}
          {item.publication_ref ? t("studio.schedule.sentToPages", { ref: item.publication_ref }) : t("studio.schedule.calendarOnly")}
        </p>
      ) : null}
      {!hasMedia ? <p className="muted">{t("studio.schedule.needMedia")}</p> : null}
      <TextField type="datetime-local" label={t("studio.fields.scheduledAt")} value={when} onChange={(e) => setWhen(e.target.value)} error={errors.scheduled_at} />
      <fieldset className="studio-channels">
        <legend>{t("studio.fields.channels")}</legend>
        {CHANNELS.map((c) => (
          <SoftToggle key={c} label={t(`studio.channel.${c}`)} checked={channels.includes(c)} onChange={(on) => toggle(c, on)} />
        ))}
      </fieldset>
      {errors.channels || errors.detail ? (
        <p role="alert" className="ao-field__error">
          {errors.channels ?? errors.detail}
        </p>
      ) : null}
      <div className="studio-row">
        <Button
          icon={<CalendarClock size={16} aria-hidden="true" />}
          disabled={!hasMedia || !when || !channels.length || schedule.isPending || item.status === "published"}
          onClick={() => schedule.mutate({ id: item.id, data: { scheduled_at: `${when}:00${DOUALA_OFFSET}`, channels } })}
        >
          {t("studio.schedule.button")}
        </Button>
        {item.status !== "published" ? (
          <Button variant="ghost" onClick={() => published.mutate({ id: item.id })}>
            {t("studio.schedule.markPublished")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function Generation({ item }: { item: ContentItem }) {
  const { t } = useTranslation();
  const refresh = useRefreshStudio();
  const visual = useStudioContentsRenderVisualCreate({ mutation: { onSuccess: refresh } });
  const video = useStudioContentsRenderVideoCreate({ mutation: { onSuccess: refresh } });
  const errors = [fieldErrors(visual.error), fieldErrors(video.error)].flatMap((e) => Object.values(e));
  const videoBusy = isBusy(item.video_state);
  const visualBusy = isBusy(item.visual_state);
  const duration = meta(item.video_meta, "duration");
  return (
    <section className="stack">
      <div className="studio-row">
        <Button icon={<ImageIcon size={16} aria-hidden="true" />} disabled={visualBusy || visual.isPending} onClick={() => visual.mutate({ id: item.id })}>
          {visualBusy ? t("studio.visual.working") : item.visual_url ? t("studio.visual.regenerate") : t("studio.visual.generate")}
        </Button>
        <Button icon={<Film size={16} aria-hidden="true" />} disabled={videoBusy || video.isPending} onClick={() => video.mutate({ id: item.id })}>
          {videoBusy ? t("studio.video.working") : item.video_url ? t("studio.video.regenerate") : t("studio.video.generate")}
        </Button>
      </div>
      {errors.length ? (
        <p role="alert" className="ao-field__error">
          {errors[0]}
        </p>
      ) : null}
      {item.visual_state === "failed" ? <p role="alert">{t("studio.visual.failed", { error: item.visual_error })}</p> : null}
      {item.visual_outdated ? <p className="muted">{t("studio.visual.outdated")}</p> : null}
      {item.visual_url && !visualBusy ? (
        <a className="studio-download" href={item.visual_url} download>
          <Download size={16} aria-hidden="true" /> {t("studio.visual.download")}
        </a>
      ) : null}
      {videoBusy ? (
        <div className="studio-progress" role="progressbar" aria-label={t("studio.video.progress")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={item.video_progress}>
          <span style={{ transform: `scaleX(${item.video_progress / 100})` }} />
        </div>
      ) : null}
      {item.video_state === "failed" ? <p role="alert">{t("studio.video.failed", { error: item.video_error })}</p> : null}
      {item.video_url && !videoBusy ? (
        <div className="stack">
          {/* The subtitles are burned into the picture (libass), so no separate caption track. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video className="studio-video" controls preload="metadata" src={item.video_url} aria-label={t("studio.video.player", { title: item.title })} />
          {duration !== undefined ? (
            <p className="muted">
              {t("studio.video.details", {
                duration,
                width: meta(item.video_meta, "width"),
                height: meta(item.video_meta, "height"),
                seconds: meta(item.video_meta, "render_seconds"),
              })}
            </p>
          ) : null}
          {item.voice_over && meta(item.video_meta, "voice") === false ? <p className="muted">{t("studio.video.voiceSkipped")}</p> : null}
          {item.video_outdated ? <p className="muted">{t("studio.video.outdated")}</p> : null}
          <a className="studio-download" href={item.video_url} download>
            <Download size={16} aria-hidden="true" /> {t("studio.video.download")}
          </a>
        </div>
      ) : null}
    </section>
  );
}

export function StudioEditor({ itemId, onClose }: { itemId: string | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshStudio();
  const [id, setId] = useState(itemId);
  const retrieve = useStudioContentsRetrieve(id ?? "", {
    query: {
      enabled: Boolean(id),
      refetchInterval: (q) => {
        const data = q.state.data;
        const row = data?.status === 200 ? data.data : undefined;
        return row && (isBusy(row.visual_state) || isBusy(row.video_state)) ? 1500 : false;
      },
    },
  });
  const item = retrieve.data?.status === 200 ? retrieve.data.data : undefined;
  const [draft, setDraft] = useState<Draft>(() => draftOf(undefined));
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (item && item.id !== loadedId) {
      setDraft(draftOf(item));
      setLoadedId(item.id);
    }
  }, [item, loadedId]);
  const offers = useWebsiteOffersList();
  const testimonials = useWebsiteTestimonialsList();
  const create = useStudioContentsCreate();
  const update = useStudioContentsPartialUpdate();
  const errors = fieldErrors(create.error ?? update.error);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const done = { onSuccess: () => (refresh(), setSaved(true)) };
    if (id) update.mutate({ id, data: payload(draft) }, done);
    else create.mutate({ data: payload(draft) }, { onSuccess: (r) => (r.status === 201 ? (setId(r.data.id), refresh(), setSaved(true)) : undefined) });
  };

  const offerRows = offers.data?.status === 200 ? offers.data.data.results : [];
  const verified = (testimonials.data?.status === 200 ? testimonials.data.data.results : []).filter((r) => r.verified_by_name && r.consent_at);
  return (
    <GlassSheet open title={id ? t("studio.editor.edit") : t("studio.editor.new")} onClose={onClose}>
      <div className="studio-editor">
        <form className="stack" onSubmit={submit}>
          <SelectField label={t("studio.kind.label")} value={draft.kind} onChange={(e) => set("kind", e.target.value as StudioKindEnum)} options={KINDS.map((k) => ({ value: k, label: t(`studio.kind.${k}`) }))} />
          {draft.kind === "offer" ? (
            <SelectField
              label={t("studio.fields.offer")}
              value={draft.offer}
              error={errors.offer}
              onChange={(e) => set("offer", e.target.value)}
              options={[{ value: "", label: t("studio.fields.chooseOffer") }, ...offerRows.map((o) => ({ value: o.id, label: `${o.code} · ${localized(o.name, i18n.language)}` }))]}
            />
          ) : null}
          {draft.kind === "testimonial" ? (
            <SelectField
              label={t("studio.fields.testimonial")}
              value={draft.testimonial}
              error={errors.testimonial}
              onChange={(e) => set("testimonial", e.target.value)}
              options={[{ value: "", label: t("studio.fields.chooseTestimonial") }, ...verified.map((r) => ({ value: r.id, label: r.name }))]}
            />
          ) : null}
          {draft.kind === "announcement" ? <TextField type="date" label={t("studio.fields.eventDate")} value={draft.event_date} onChange={(e) => set("event_date", e.target.value)} /> : null}
          <TextField
            label={t("studio.fields.title")}
            value={draft.title}
            error={errors.title}
            hint={draft.kind === "offer" || draft.kind === "testimonial" ? t("studio.fields.prefillHint") : undefined}
            onChange={(e) => set("title", e.target.value)}
          />
          <TextAreaField label={t("studio.fields.text")} rows={5} value={draft.text} error={errors.text} onChange={(e) => set("text", e.target.value)} />
          <p className="muted">{t("studio.compliance")}</p>
          <div className="studio-grid">
            <SelectField label={t("studio.language.label")} value={draft.language} onChange={(e) => set("language", e.target.value === "en" ? "en" : "fr")} options={(["fr", "en"] as const).map((l) => ({ value: l, label: t(`studio.language.${l}`) }))} />
            <SelectField label={t("studio.template.label")} value={draft.template} onChange={(e) => set("template", e.target.value as StudioTemplateEnum)} options={TEMPLATES.map((v) => ({ value: v, label: t(`studio.template.${v}`) }))} />
            <SelectField label={t("studio.theme.label")} value={draft.theme} onChange={(e) => set("theme", e.target.value as StudioThemeEnum)} options={THEMES.map((v) => ({ value: v, label: t(`studio.theme.${v}`) }))} />
            <SelectField label={t("studio.music.label")} value={draft.music} onChange={(e) => set("music", e.target.value as StudioMusicEnum)} options={MUSICS.map((v) => ({ value: v, label: t(`studio.music.${v}`) }))} />
          </div>
          <SoftToggle label={t("studio.fields.voiceOver")} checked={draft.voice_over} onChange={(on) => set("voice_over", on)} />
          {errors.detail ? (
            <p role="alert" className="ao-field__error">
              {errors.detail}
            </p>
          ) : null}
          <div className="studio-row">
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {t("studio.editor.save")}
            </Button>
            {saved ? <span role="status">{t("studio.editor.saved")}</span> : null}
          </div>
          {item?.notes.length ? (
            <section aria-label={t("studio.editor.notes")}>
              <h3>{t("studio.editor.notes")}</h3>
              <ul>
                {item.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </form>
        <div className="stack">
          <h3>{t("studio.editor.preview")}</h3>
          <Preview item={item} draft={draft} />
          {item ? <Generation item={item} /> : null}
          {item ? <ScheduleBox item={item} /> : null}
          {item?.event_date ? <p className="muted">{dateText(item.event_date, i18n.language)}</p> : null}
        </div>
      </div>
    </GlassSheet>
  );
}

// --- Page -------------------------------------------------------------------------------------

export function StudioPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("calendar");
  const [open, setOpen] = useState<{ id: string | null } | null>(null);
  return (
    <Page
      title={t("studio.title")}
      description={t("studio.description")}
      actions={
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setOpen({ id: null })}>
          {t("studio.new")}
        </Button>
      }
    >
      <div className="stack">
        <div role="tablist" aria-label={t("studio.tabs.label")} className="tabs">
          {TABS.map((key) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={`tabs__tab${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
              {t(`studio.tabs.${key}`)}
            </button>
          ))}
        </div>
        {tab === "calendar" ? <CalendarTab onOpen={(item) => setOpen({ id: item.id })} /> : null}
        {tab === "list" ? <ListTab onOpen={(item) => setOpen({ id: item.id })} onNew={() => setOpen({ id: null })} /> : null}
        {tab === "ideas" ? <IdeasTab onOpen={(item) => setOpen({ id: item.id })} /> : null}
      </div>
      {open ? <StudioEditor key={open.id ?? "new"} itemId={open.id} onClose={() => setOpen(null)} /> : null}
    </Page>
  );
}
